import React, { useState, useEffect } from 'react';
import EventoService from '../services/eventoService';
import CrearEventoModal from './CrearEventoModal.js';
import ModificarEventoModal from './ModificarEventoModal.js';
import CustomModal from './CustomModal';
import useCustomModal from '../hooks/useCustomModal';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Typography, IconButton, Button } from '@mui/material';
import { useCalendario } from '../contexts/CalendarioContext';
import '../style/components/CalendarioLateral.css';

const getFeriadoColor = (tipo) => {
    const colores = {
        'NACIONAL':     '#3B82F6',
        'INAMOVIBLE':   '#3B82F6',
        'TRASLADABLE':  '#8B5CF6',
        'PUENTE':       '#F59E0B',
        'NO_LABORABLE': '#FB923C',
        'PROVINCIAL':   '#64748B',
        'LOCAL':        '#0EA5E9',
        'JUDICIAL':     '#10B981',
    };
    return colores[tipo?.toUpperCase()] || '#3B82F6';
};

const CalendarToolContent = () => {
    const { feriados } = useCalendario();

    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(null);
    const [appointments, setAppointments] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isModificarModalOpen, setIsModificarModalOpen] = useState(false);
    const [selectedDayEvents, setSelectedDayEvents] = useState([]);
    const [selectedDayDate, setSelectedDayDate] = useState(null);
    const [eventoToEdit, setEventoToEdit] = useState(null);
    const [isLoadingEvents, setIsLoadingEvents] = useState(false);

    const { modalState, hideModal, showSuccess, showError, showConfirm } = useCustomModal();

    const fetchAppointments = async () => {
        try {
            setIsLoadingEvents(true);
            const response = await EventoService.getEventos();
            const d = response?.data ?? response;
            const arr = Array.isArray(d) ? d : (Array.isArray(d?.content) ? d.content : []);
            const formattedAppointments = arr.map(app => ({
                id: app.idEvento,
                date: app.fecha,
                time: app.horaInicio,
                client: app.titulo,
                type: app.tipoEvento,
                description: app.descripcion,
                todoElDia: app.todoElDia,
                idTramite: app.idTramite,
                horaInicio: app.horaInicio,
                horaFin: app.horaFin,
                nombreCompletoCliente: app.nombreCompletoCliente || null,
                cuilCliente: app.cuilCliente || null
            }));
            setAppointments(formattedAppointments);
        } catch (error) {
            console.error("Error al cargar eventos:", error);
        } finally {
            setIsLoadingEvents(false);
        }
    };

    useEffect(() => {
        fetchAppointments();
    }, []);

    const handleSaveEvento = async (eventoData) => {
        try {
            await EventoService.crearEvento(eventoData);
            setIsModalOpen(false);
            await fetchAppointments();
            showSuccess("Evento creado correctamente");
        } catch (error) {
            showError("Error al crear el evento");
        }
    };

    const handleModificarEvento = async (idEvento, eventoData) => {
        try {
            await EventoService.modificarEvento(idEvento, eventoData);
            setIsModificarModalOpen(false);
            setEventoToEdit(null);
            showSuccess("Evento modificado correctamente");
            await fetchAppointments();
        } catch (error) {
            showError("Error al modificar el evento");
        }
    };

    const handleEliminarEvento = (idEvento) => {
        showConfirm('¿Estás seguro?', async () => {
            try {
                await EventoService.eliminarEvento(idEvento);
                showSuccess('Evento eliminado');
                await fetchAppointments();
            } catch (error) {
                showError('Error al eliminar');
            }
        });
    };

    const changeMonth = (increment) => {
        setCurrentDate(prev => {
            const next = new Date(prev);
            next.setMonth(prev.getMonth() + increment);
            return next;
        });
    };

    const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
    const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

    const getAppointmentsForDate = (date) => {
        const s = `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
        return appointments.filter(app => app.date === s);
    };

    const getFeriadoForDate = (date) => {
        const s = `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
        return feriados.find(f => f.fecha === s);
    };

    const handleDateClick = (day) => {
        const clicked = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
        setSelectedDate(clicked);
        setSelectedDayDate(clicked);
        setSelectedDayEvents(getAppointmentsForDate(clicked));
    };

    const renderCalendar = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const daysInMonth = getDaysInMonth(year, month);
        const firstDay = getFirstDayOfMonth(year, month);
        const days = [];

        for (let i = 0; i < firstDay; i++) {
            days.push(<div key={`empty-${i}`} className="calendar-day empty" />);
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            const dayApps = getAppointmentsForDate(date);
            const feriado = getFeriadoForDate(date);
            const isToday = new Date().toDateString() === date.toDateString();
            const isSelected = selectedDate && selectedDate.toDateString() === date.toDateString();
            const isFeriado = !!feriado;
            const feriadoColor = isFeriado ? getFeriadoColor(feriado.tipo) : null;

            const feriadoStyle = isFeriado && !isToday && !isSelected ? {
                backgroundColor: `${feriadoColor}26`,
                borderColor: feriadoColor,
                borderWidth: '2px',
                borderStyle: 'solid',
            } : {};

            days.push(
                <div
                    key={day}
                    className={`calendar-day ${dayApps.length > 0 ? 'has-appointments' : ''} ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
                    style={feriadoStyle}
                    onClick={() => handleDateClick(day)}
                    title={isFeriado ? feriado.descripcion : ''}
                >
                    <div className="day-number" style={isFeriado && !isToday && !isSelected ? { color: feriadoColor, fontWeight: 700 } : {}}>
                        {day}
                        {isFeriado && <span style={{ fontSize: '0.6rem', marginLeft: 2 }}>●</span>}
                    </div>
                    {dayApps.length > 0 && <div className="appointment-indicator">{dayApps.length}</div>}
                </div>
            );
        }
        return days;
    };

    const formatTime = (time) => time ? time.toString().substring(0, 5) : '';

    // Próximos feriados del mes actual
    const feriadosDelMes = feriados.filter(f => {
        const [y, m] = f.fecha.split('-').map(Number);
        return y === currentDate.getFullYear() && m === currentDate.getMonth() + 1;
    });

    return (
        <div className="calendar-tool-content">
            <div className="calendar-section">
                <div style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10 }}>
                    <div className="section-header">
                        <div className="month-navigation">
                            <button onClick={() => changeMonth(-1)}>‹</button>
                            <span>{currentDate.toLocaleString('es-ES', { month: 'long', year: 'numeric' }).toUpperCase()}</span>
                            <button onClick={() => changeMonth(1)}>›</button>
                        </div>
                    </div>
                    <div className="calendar-grid" style={{ paddingBottom: 0 }}>
                        {['D','L','M','M','J','V','S'].map((d, i) => (
                            <div key={i} className="weekday">{d}</div>
                        ))}
                    </div>
                </div>

                <div className="calendar-grid" style={{ paddingTop: 0 }}>
                    {renderCalendar()}
                </div>
            </div>

            {/* Feriados del mes */}
            {feriadosDelMes.length > 0 && (
                <Box sx={{ mt: 1, mb: 1, px: 1 }}>
                    {feriadosDelMes.map(f => {
                        const color = getFeriadoColor(f.tipo);
                        const [, , dd] = f.fecha.split('-').map(Number);
                        return (
                            <Box
                                key={f.fecha}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1,
                                    py: 0.4, px: 0.75, mb: 0.3, borderRadius: 1,
                                    borderLeft: `3px solid ${color}`,
                                    backgroundColor: `${color}12`,
                                }}
                            >
                                <Typography variant="caption" sx={{ color, fontWeight: 700, minWidth: 24 }}>
                                    {String(dd).padStart(2, '0')}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#374151', flex: 1 }} noWrap>
                                    {f.descripcion}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            )}

            <Box sx={{ mt: 1, mb: 2, px: 1 }}>
                <Button fullWidth variant="contained" size="small" onClick={() => setIsModalOpen(true)} sx={{ textTransform: 'none' }}>
                    + Nuevo Evento
                </Button>
            </Box>

            {selectedDayDate && (
                <div className="selected-day-details">
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        {selectedDayDate.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
                    </Typography>
                    {/* Feriado del día seleccionado */}
                    {(() => {
                        const f = getFeriadoForDate(selectedDayDate);
                        if (!f) return null;
                        const color = getFeriadoColor(f.tipo);
                        return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, px: 1, mb: 1, borderRadius: 1, borderLeft: `4px solid ${color}`, backgroundColor: `${color}15` }}>
                                <Typography variant="caption" sx={{ color, fontWeight: 700 }}>🎉 Feriado</Typography>
                                <Typography variant="caption" sx={{ color: '#374151' }}>{f.descripcion}</Typography>
                            </Box>
                        );
                    })()}
                    <div className="selected-day-events">
                        {selectedDayEvents.length > 0 ? selectedDayEvents.map((event, i) => (
                            <div key={i} className="selected-day-event-item" style={{ padding: '8px', marginBottom: '8px' }}>
                                <div className="event-content">
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{event.client}</Typography>
                                    <Typography variant="caption" color="text.secondary">{formatTime(event.horaInicio)} — {event.type}</Typography>
                                </div>
                                <div className="event-actions">
                                    <IconButton size="small" onClick={() => { setEventoToEdit(event); setIsModificarModalOpen(true); }}><EditIcon fontSize="inherit" /></IconButton>
                                    <IconButton size="small" onClick={() => handleEliminarEvento(event.id)}><DeleteIcon fontSize="inherit" /></IconButton>
                                </div>
                            </div>
                        )) : (
                            <Typography variant="caption" color="text.secondary">No hay eventos para este día.</Typography>
                        )}
                    </div>
                </div>
            )}

            <CrearEventoModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSave={handleSaveEvento} selectedDate={selectedDate || new Date()} />
            <ModificarEventoModal isOpen={isModificarModalOpen} onClose={() => setIsModificarModalOpen(false)} onSave={handleModificarEvento} evento={eventoToEdit} />
            <CustomModal show={modalState.show} onHide={hideModal} title={modalState.title} message={modalState.message} type={modalState.type} onConfirm={modalState.onConfirm} />
        </div>
    );
};

export default CalendarToolContent;
